import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { buildPaymentScheduleBreakdown } from '../../utils/paymentSchedule';
import { computePaymentDueForecast, type MilestoneLike } from '../../utils/paymentDueFlags';
import './ProjectTimeline.css';

export interface TimelinePhase {
  id: string;
  label: string;
  durationWeeks: number;
  bufferWeeks: number;
  subPhases?: Array<{
    id: string;
    label: string;
    durationWeeks: number;
  }>;
}

export interface TimelineMilestone {
  label: string;
  phaseId: string;
  anchor: 'start' | 'end';
}

/** Date-accurate payment flag (post-signing, from the payment forecast). */
export interface TimelinePaymentDueFlag {
  label: string;
  date: Date;
  estimated?: boolean;
}

interface ProjectTimelineProps {
  phases?: TimelinePhase[];
  milestones?: TimelineMilestone[];
  savedTargetDate?: Date | null;
  onTargetDateChange?: (date: Date | null) => void;
  /**
   * Real payment due dates from the contract forecast. When provided, these
   * replace the phase-derived estimates and position by actual date.
   */
  paymentDueFlags?: TimelinePaymentDueFlag[];
}

const DEFAULT_PHASES: TimelinePhase[] = [
  { id: '2d-plans', label: 'Engineered Drawings', durationWeeks: 3, bufferWeeks: 1 },
  {
    id: 'in-production', label: 'Production', durationWeeks: 8, bufferWeeks: 1,
    subPhases: [
      { id: 'material-lead', label: 'Material Lead Time', durationWeeks: 6 },
      { id: 'cnc-production', label: 'CNC Production', durationWeeks: 2 },
    ],
  },
  { id: 'delivery', label: 'Delivery & Install', durationWeeks: 2, bufferWeeks: 0 },
];

type TimelineMode = 'forward' | 'reverse';

function addWeeks(date: Date, weeks: number): Date {
  const d = new Date(date);
  d.setDate(d.getDate() + weeks * 7);
  return d;
}

function fmtDate(date: Date): string {
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function toInputDate(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** Shorter labels when a phase column is too narrow for the full name. */
const TIMELINE_LABEL_ABBREV: Record<string, string> = {
  'Engineered Drawings': 'Drawings',
  'Material Lead Time': 'Mat. Lead',
  'CNC Production': 'CNC',
  'Delivery & Install': 'Delivery',
  '3D Model': '3D Model',
  Production: 'Production',
};

function displayTimelineLabel(label: string, segmentWeeks: number, timelineTotalWeeks: number): string {
  if (timelineTotalWeeks <= 0 || segmentWeeks <= 0) return label;
  const ratio = segmentWeeks / timelineTotalWeeks;
  if (ratio >= 0.16) return label;
  return TIMELINE_LABEL_ABBREV[label] ?? (label.length > 14 ? label.replace(/\s+&\s+/g, ' ').split(' ').slice(0, 2).join(' ') : label);
}

function computeForward(phases: TimelinePhase[], start: Date) {
  let cursor = new Date(start);
  return phases.map(phase => {
    const phaseStart = new Date(cursor);
    const phaseEnd = addWeeks(cursor, phase.durationWeeks);
    let subComputed: Array<{ id: string; label: string; durationWeeks: number; start: Date; end: Date }> | undefined;
    if (phase.subPhases?.length) {
      let subCursor = new Date(phaseStart);
      subComputed = phase.subPhases.map(sub => {
        const subStart = new Date(subCursor);
        const subEnd = addWeeks(subCursor, sub.durationWeeks);
        subCursor = subEnd;
        return { ...sub, start: subStart, end: subEnd };
      });
    }
    cursor = phaseEnd;
    if (phase.bufferWeeks > 0) cursor = addWeeks(cursor, phase.bufferWeeks);
    return { ...phase, startDate: phaseStart, endDate: phaseEnd, subComputed };
  });
}

interface PositionedFlag {
  label: string;
  date: Date;
  position: number; // 0–100% horizontal position
}

const INSTALLMENT_FLAG_LABELS: Record<string, string> = {
  deposit: '50% — On signing',
  pre_shipment: '40% — Before shipment',
  final: '10% — After install',
};

/**
 * Estimated payment flags via the shared forecast util: phases are mapped to
 * pseudo anchor milestones ("Shipment Date" at production end, "Delivery
 * Complete" at completion) so the anchor logic matches the staff Gantt.
 */
function deriveDefaultPayments(
  start: Date,
  computed: ReturnType<typeof computeForward>,
  completion: Date,
): PositionedFlag[] {
  const totalMs = completion.getTime() - start.getTime();
  const pct = (d: Date) => totalMs > 0 ? Math.max(0, Math.min(100, ((d.getTime() - start.getTime()) / totalMs) * 100)) : 0;

  const productionPhase = computed.find(p => p.id === 'in-production');
  const pseudoMilestones: MilestoneLike[] = [];
  if (productionPhase) {
    pseudoMilestones.push({ id: 'in-production', title: 'Shipment Date', targetDate: productionPhase.endDate });
  }
  pseudoMilestones.push({ id: 'completion', title: 'Delivery Complete', targetDate: completion });

  const forecast = computePaymentDueForecast(
    { contractSignedAt: start, startDate: start, targetEndDate: completion },
    pseudoMilestones,
    buildPaymentScheduleBreakdown(0, []),
  );

  return forecast.map(f => {
    const date = f.dueDate ?? completion;
    return {
      label: INSTALLMENT_FLAG_LABELS[f.installmentId] ?? f.label,
      date,
      position: pct(date),
    };
  });
}

function deriveMilestoneFlags(
  milestones: TimelineMilestone[],
  computed: ReturnType<typeof computeForward>,
  start: Date,
  completion: Date,
): PositionedFlag[] {
  const totalMs = completion.getTime() - start.getTime();
  const pct = (d: Date) => totalMs > 0 ? Math.max(0, Math.min(100, ((d.getTime() - start.getTime()) / totalMs) * 100)) : 0;

  return milestones.map(m => {
    const phase = computed.find(p => p.id === m.phaseId);
    const date = phase
      ? (m.anchor === 'start' ? phase.startDate : phase.endDate)
      : start;
    return { label: m.label, date, position: pct(date) };
  });
}

const ProjectTimeline: React.FC<ProjectTimelineProps> = ({ phases, milestones, savedTargetDate, onTargetDateChange, paymentDueFlags }) => {
  const resolvedPhases = phases && phases.length > 0 ? phases : DEFAULT_PHASES;
  const containerRef = useRef<HTMLDivElement>(null);
  const [animated, setAnimated] = useState(false);
  const [mode, setMode] = useState<TimelineMode>(savedTargetDate ? 'reverse' : 'forward');

  const totalWeeks = resolvedPhases.reduce((s, p) => s + p.durationWeeks + p.bufferWeeks, 0);

  const defaultTarget = useMemo(() => addWeeks(new Date(), totalWeeks + 4), [totalWeeks]);
  const [targetDate, setTargetDate] = useState<Date>(savedTargetDate || defaultTarget);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setAnimated(true); obs.disconnect(); } },
      { threshold: 0.3 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const { startDate, completionDate, computed } = useMemo(() => {
    if (mode === 'forward') {
      const today = new Date();
      return { startDate: today, completionDate: addWeeks(today, totalWeeks), computed: computeForward(resolvedPhases, today) };
    }
    const reverseStart = addWeeks(targetDate, -totalWeeks);
    return { startDate: reverseStart, completionDate: targetDate, computed: computeForward(resolvedPhases, reverseStart) };
  }, [mode, resolvedPhases, targetDate, totalWeeks]);

  const flags = useMemo(() => {
    // Date-accurate flags from the contract payment forecast take precedence.
    if (paymentDueFlags && paymentDueFlags.length > 0) {
      const totalMs = completionDate.getTime() - startDate.getTime();
      const pct = (d: Date) =>
        totalMs > 0 ? Math.max(0, Math.min(100, ((d.getTime() - startDate.getTime()) / totalMs) * 100)) : 0;
      return paymentDueFlags.map(f => ({ label: f.label, date: f.date, position: pct(f.date) }));
    }
    return milestones
      ? deriveMilestoneFlags(milestones, computed, startDate, completionDate)
      : deriveDefaultPayments(startDate, computed, completionDate);
  }, [paymentDueFlags, milestones, startDate, computed, completionDate]);

  const isPastDeadline = mode === 'reverse' && startDate < new Date();

  const switchMode = useCallback((next: TimelineMode) => {
    if (next === mode) return;
    setMode(next);
    if (next === 'forward') onTargetDateChange?.(null);
  }, [mode, onTargetDateChange]);

  const handleDateChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    if (!val) return;
    const parts = val.split('-').map(Number);
    if (parts.length !== 3 || parts.some(isNaN)) return;
    const [y, m, d] = parts;
    const date = new Date(y, m - 1, d);
    if (isNaN(date.getTime())) return;
    setTargetDate(date);
    onTargetDateChange?.(date);
  }, [onTargetDateChange]);

  return (
    <div ref={containerRef} className={`pt-timeline ${animated ? 'animate' : ''} ${mode === 'reverse' ? 'pt-reverse' : ''}`}>
      {/* Mode tabs + headline */}
      <div className="pt-header">
        <div className="pt-tabs">
          <button className={`pt-tab ${mode === 'forward' ? 'active' : ''}`} onClick={() => switchMode('forward')}>
            <span className="pt-tab-dot" />
            Starting today
          </button>
          <button className={`pt-tab ${mode === 'reverse' ? 'active' : ''}`} onClick={() => switchMode('reverse')}>
            <span className="pt-tab-dot" />
            I have a deadline
          </button>
        </div>

        <div className="pt-summary">
          {mode === 'forward' ? (
            <>
              <span className="pt-summary-text">
                Installed in as few as <strong>{totalWeeks} weeks</strong>
              </span>
              <span className="pt-summary-badge">
                Ready by <strong>{fmtDate(completionDate)}</strong>
              </span>
            </>
          ) : (
            <>
              <span className="pt-summary-text">
                {isPastDeadline ? (
                  <span className="pt-warn-text">You'd need to have started already</span>
                ) : (
                  <>Start by <strong>{fmtDate(startDate)}</strong> to finish on time</>
                )}
              </span>
              <label className="pt-date-picker">
                <span className="pt-date-label">Finish by</span>
                <input
                  type="date"
                  className="pt-date-input"
                  value={toInputDate(targetDate)}
                  min={toInputDate(addWeeks(new Date(), 1))}
                  onChange={handleDateChange}
                />
              </label>
            </>
          )}
        </div>
      </div>

      {/* Gantt bars with flag overlays */}
      <div className="pt-gantt-wrap">
        {/* The bar row — flags position relative to this */}
        <div className="pt-bars-container">
          <div className="pt-bars">
            {computed.map(phase => {
              const hasSubPhases = phase.subComputed && phase.subComputed.length > 0;
              return (
                <React.Fragment key={phase.id}>
                  <div className={`pt-phase pt-phase-${phase.id}`} style={{ flex: phase.durationWeeks }}>
                    {hasSubPhases ? (
                      <>
                        <div className="pt-sub-container">
                          {phase.subComputed!.map(sub => (
                            <div
                              key={sub.id}
                              className={`pt-sub-bar pt-sub-${sub.id}`}
                              style={{ flex: sub.durationWeeks }}
                              title={`${sub.label}: ${fmtDate(sub.start)} – ${fmtDate(sub.end)}`}
                            >
                              <span className="pt-bar-weeks">{sub.durationWeeks}wk</span>
                            </div>
                          ))}
                        </div>
                        <div className="pt-sub-labels">
                          {phase.subComputed!.map(sub => (
                            <div key={sub.id} className="pt-sub-label" style={{ flex: sub.durationWeeks }}>
                              <span
                                className="pt-label-name"
                                title={sub.label}
                              >
                                {displayTimelineLabel(sub.label, sub.durationWeeks, totalWeeks)}
                              </span>
                              <span className="pt-label-date">{fmtDate(sub.start)}</span>
                            </div>
                          ))}
                        </div>
                      </>
                    ) : (
                      <>
                        <div
                          className="pt-phase-bar"
                          title={`${phase.label}: ${fmtDate(phase.startDate)} – ${fmtDate(phase.endDate)}`}
                        >
                          <span className="pt-bar-weeks">{phase.durationWeeks}wk</span>
                        </div>
                        <div className="pt-phase-label">
                          <span
                            className="pt-label-name"
                            title={phase.label}
                          >
                            {displayTimelineLabel(phase.label, phase.durationWeeks, totalWeeks)}
                          </span>
                          <span className="pt-label-date">{fmtDate(phase.startDate)}</span>
                        </div>
                      </>
                    )}
                  </div>

                  {phase.bufferWeeks > 0 && (
                    <div className="pt-buffer" style={{ flex: phase.bufferWeeks }} title={`${phase.bufferWeeks}wk buffer`}>
                      <div className="pt-buffer-tick" />
                    </div>
                  )}
                </React.Fragment>
              );
            })}
          </div>

          {/* Flags — absolutely positioned within bars-container (desktop) */}
          {flags.map(f => (
            <div
              key={f.label}
              className="pt-flag"
              style={{ left: `${f.position}%` }}
            >
              <svg className="pt-flag-svg" viewBox="0 0 22 56" fill="none" xmlns="http://www.w3.org/2000/svg">
                <line x1="2" y1="0" x2="2" y2="56" stroke="#b8a97a" strokeWidth="1.4" />
                <rect x="3" y="1" width="17" height="12" rx="1" fill="#d4c9a8" stroke="#c4b890" strokeWidth="0.4" />
                <text x="11.5" y="10" textAnchor="middle" fontSize="8" fontWeight="700" fill="#7a6e4e" fontFamily="system-ui">$</text>
              </svg>
              <span className="pt-flag-tip">{f.label}<br />{fmtDate(f.date)}</span>
            </div>
          ))}
        </div>

        {/* Mobile payment schedule (replaces hidden flags) */}
        <div className="pt-payments-mobile">
          {flags.map((f, i) => (
            <div key={i} className="pt-pay-row">
              <span className="pt-pay-icon">$</span>
              <span className="pt-pay-label">{f.label}</span>
              <span className="pt-pay-date">{fmtDate(f.date)}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ProjectTimeline;
